/** SUNTRONIX ONE — Order-Level Profitability & Revenue Leakage. */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  TrendingUp, TrendingDown, RefreshCcw, Plus, Wallet,
  AlertTriangle, DollarSign, Package, Layers,
} from "lucide-react";

const STATUS_TONE = {
  PROFITABLE:            "bg-emerald-100 text-emerald-800",
  BELOW_TARGET_MARGIN:   "bg-amber-100 text-amber-900",
  LOW_MARGIN:            "bg-amber-200 text-amber-900",
  BREAK_EVEN:            "bg-slate-100 text-slate-700",
  NEGATIVE_MARGIN:       "bg-rose-100 text-rose-800",
  REVENUE_NOT_BILLED:    "bg-rose-100 text-rose-800",
  COST_INCOMPLETE:       "bg-slate-100 text-slate-700",
  PROVISIONAL:           "bg-sky-100 text-sky-800",
  REVIEW_REQUIRED:       "bg-slate-100 text-slate-700",
};

const LEAK_TONE = {
  IDENTIFIED:                "bg-amber-100 text-amber-900",
  UNDER_REVIEW:              "bg-amber-100 text-amber-900",
  RECOVERABLE:               "bg-sky-100 text-sky-800",
  RECOVERY_IN_PROGRESS:      "bg-sky-100 text-sky-800",
  RECOVERED:                 "bg-emerald-100 text-emerald-800",
  PARTIALLY_RECOVERED:       "bg-teal-100 text-teal-800",
  NOT_RECOVERABLE:           "bg-rose-100 text-rose-800",
  PREVENTION_ACTION_REQUIRED:"bg-amber-200 text-amber-900",
  RESOLVED:                  "bg-emerald-100 text-emerald-800",
  CANCELLED:                 "bg-slate-200 text-slate-500",
};

const COST_CATEGORIES = [
  "product_procurement", "courier_forward", "courier_reverse",
  "courier_reroute", "failed_delivery", "failed_pickup",
  "packaging_material", "warehouse_inward", "warehouse_storage",
  "put_away", "pick_pack", "warehouse_labour",
  "field_engineer", "onsite_support", "remote_support",
  "service_centre", "repair", "spare_parts",
  "vendor_partner", "mdm", "applecare_plus", "software_licence",
  "data_erasure", "erasure_certificate", "itad",
  "recycling", "destruction", "buyback_settlement",
  "insurance", "customs_duties", "freight",
  "forex_variance", "bank_transaction", "customer_credit",
  "service_rework", "replacement", "damage_loss", "other_approved",
];

const TABS = [
  { id: "overview",       label: "Overview" },
  { id: "orders",         label: "Order Profitability" },
  { id: "services",       label: "Service Profitability" },
  { id: "customers",      label: "Customer Profitability" },
  { id: "costs",          label: "Cost Capture" },
  { id: "unallocated",    label: "Unallocated Costs" },
  { id: "leakage",        label: "Revenue Leakage" },
  { id: "rate",           label: "Rate Adequacy" },
  { id: "forecast",       label: "Forecast vs Actual" },
  { id: "reports",        label: "Reports" },
  { id: "history",        label: "History" },
];

const fmt = (n, ccy = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy || "INR", maximumFractionDigits: 2 }).format(n || 0);


export default function Profitability() {
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAddCost, setShowAddCost] = useState(false);
  const [showAddLeak, setShowAddLeak] = useState(false);
  const [orderProbe, setOrderProbe] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [costs, setCosts] = useState([]);
  const [unallocated, setUnallocated] = useState({ items: [], total_amount: 0 });
  const [leakage, setLeakage] = useState([]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [dash, cust, svc, cs, un, lk] = await Promise.all([
        api.get("/profitability/dashboard"),
        api.get("/profitability/customers"),
        api.get("/profitability/services"),
        api.get("/profitability/costs"),
        api.get("/profitability/unallocated"),
        api.get("/profitability/leakage"),
      ]);
      setDashboard(dash.data);
      setCustomers(cust.data.items || []);
      setServices(svc.data.items || []);
      setCosts(cs.data.items || []);
      setUnallocated(un.data);
      setLeakage(lk.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const probeOrder = async () => {
    if (!orderProbe.trim()) { toast.error("Enter an Order ID"); return; }
    try {
      const { data } = await api.get(`/profitability/orders/${orderProbe.trim()}`);
      setOrderData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Not found");
      setOrderData(null);
    }
  };

  return (
    <div className="space-y-6 max-w-[1500px]" data-testid="profitability">
      <PageHeader
        eyebrow="Commercial · Profitability & Leakage"
        title="Order-Level Costing, Profitability & Revenue Leakage"
        context="Every Order ID gets a full commercial story — revenue, direct cost, contribution margin, target adherence and where revenue leaked. Internal to Suntronix only; customers never see any of this."
        actions={
          <Button variant="outline" onClick={load} disabled={busy}
                  data-testid="profit-refresh-btn">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      {/* Dashboard tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Tile testId="profit-tile-revenue" label="Revenue (MTD)"
              value={fmt(dashboard?.monthly_revenue)} tone="text-emerald-700" icon={DollarSign} />
        <Tile testId="profit-tile-cost" label="Direct cost"
              value={fmt(dashboard?.monthly_cost)} tone="text-rose-700" icon={Wallet} />
        <Tile testId="profit-tile-margin" label="Contribution margin"
              value={fmt(dashboard?.contribution_margin)} tone={(dashboard?.contribution_margin || 0) >= 0 ? "text-emerald-700" : "text-rose-700"} icon={(dashboard?.contribution_margin || 0) >= 0 ? TrendingUp : TrendingDown} />
        <Tile testId="profit-tile-margin-pct" label="Margin %"
              value={`${dashboard?.margin_pct?.toFixed(1) || 0}%`}
              tone={(dashboard?.margin_pct || 0) >= 25 ? "text-emerald-700" : "text-amber-800"} icon={TrendingUp} />
        <Tile testId="profit-tile-unallocated" label="Unallocated costs"
              value={fmt(dashboard?.unallocated_costs)} sub={`${dashboard?.unallocated_count || 0} item(s)`} tone="text-amber-800" icon={Package} />
        <Tile testId="profit-tile-leakage" label="Revenue leakage"
              value={fmt(dashboard?.revenue_leakage)} sub={`${dashboard?.leakage_open || 0} open`} tone="text-rose-700" icon={AlertTriangle} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-[12px] font-medium tracking-wide border-b-2 -mb-px whitespace-nowrap transition-colors
              ${tab === t.id
                ? "border-copper-600 text-copper-700"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`profit-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab customers={customers} services={services} leakage={leakage} />
      )}
      {tab === "orders" && (
        <OrderProbeTab orderProbe={orderProbe} setOrderProbe={setOrderProbe}
                        onProbe={probeOrder} orderData={orderData} />
      )}
      {tab === "services" && <ServiceTable rows={services} />}
      {tab === "customers" && <CustomerTable rows={customers} />}
      {tab === "costs" && <CostsTable rows={costs} onReload={load}
                                          onAdd={() => setShowAddCost(true)} />}
      {tab === "unallocated" && <UnallocatedTable data={unallocated} />}
      {tab === "leakage" && <LeakageTable rows={leakage} onReload={load}
                                              onAdd={() => setShowAddLeak(true)} />}
      {(tab === "rate" || tab === "forecast" || tab === "reports" || tab === "history") && (
        <ComingSoon tab={tab} />
      )}

      {showAddCost && (
        <AddCostDialog onClose={() => setShowAddCost(false)}
                        onAdded={() => { setShowAddCost(false); load(); }} />
      )}
      {showAddLeak && (
        <AddLeakageDialog onClose={() => setShowAddLeak(false)}
                            onAdded={() => { setShowAddLeak(false); load(); }} />
      )}
    </div>
  );
}


function Tile({ label, value, sub, tone, icon: Icon, testId }) {
  return (
    <div className="st-panel p-3" data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </div>
      <div className={`text-xl font-heading font-semibold tabular-nums truncate ${tone || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}


function OverviewTab({ customers, services, leakage }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Section title="Top customers by revenue" testId="profit-overview-customers">
        <MiniCustomerTable rows={customers.slice(0, 6)} />
      </Section>
      <Section title="Top services by revenue" testId="profit-overview-services">
        <MiniServiceTable rows={services.slice(0, 6)} />
      </Section>
      <Section title="Recent revenue leakage" testId="profit-overview-leakage">
        <div className="st-panel divide-y divide-slate-100">
          {leakage.slice(0, 6).map((l) => (
            <div key={l.id} className="p-3 text-[12px]"
                 data-testid={`profit-overview-leak-${l.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{l.customer_name || l.master_company_id}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${LEAK_TONE[l.status]}`}>
                  {l.status.replace(/_/g, " ")}
                </span>
              </div>
              <div className="text-[11px] text-slate-500">
                {l.order_id || "—"} · {l.leakage_type.replace(/_/g, " ")}
              </div>
              <div className="text-[11px] font-semibold text-rose-800">
                {fmt(l.estimated_value, l.currency)} estimated
              </div>
            </div>
          ))}
          {leakage.length === 0 && (
            <div className="p-6 text-center text-[12px] text-slate-400">
              No leakage records — every rupee is captured.
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}


function OrderProbeTab({ orderProbe, setOrderProbe, onProbe, orderData }) {
  return (
    <Section title="Order profitability lookup"
             hint="Type an Order ID (e.g. ORD-4001 from the demo seed) and press Look up."
             testId="profit-orders-section">
      <div className="flex gap-2 mb-3">
        <Input value={orderProbe} onChange={(e) => setOrderProbe(e.target.value)}
               placeholder="Enter Order ID"
               className="max-w-xs" data-testid="profit-order-input" />
        <Button onClick={onProbe} data-testid="profit-order-lookup-btn">Look up</Button>
      </div>
      {orderData && (
        <div className="st-panel p-4 space-y-4" data-testid="profit-order-card">
          <div className="flex items-center gap-3">
            <div className="font-mono text-[13px]">{orderData.order_id}</div>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${STATUS_TONE[orderData.profitability_status]}`}>
              {orderData.profitability_status.replace(/_/g, " ")}
            </span>
            <div className="ml-auto text-[11px] text-slate-500">{orderData.order?.customer_name} · {orderData.order?.service_code}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[13px]">
            <Field label="Revenue" value={fmt(orderData.revenue, orderData.currency)} bold tone="text-emerald-700" />
            <Field label="Actual cost" value={fmt(orderData.actual_cost, orderData.currency)} />
            <Field label="Estimated" value={fmt(orderData.estimated_cost, orderData.currency)} />
            <Field label="Contribution" value={fmt(orderData.contribution_margin, orderData.currency)} bold
                    tone={orderData.contribution_margin >= 0 ? "text-emerald-700" : "text-rose-700"} />
            <Field label="Margin %" value={`${orderData.margin_pct}%`} bold />
            <Field label="Target / Variance" value={`${orderData.target_margin_pct}% (${orderData.variance_pct >= 0 ? "+" : ""}${orderData.variance_pct}%)`} />
          </div>
          {orderData.missing_categories?.length > 0 && (
            <div className="text-[11px] text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              Cost still missing: <span className="font-mono">{orderData.missing_categories.join(", ")}</span>
            </div>
          )}
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2">Cost breakdown</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Net</th>
                </tr>
              </thead>
              <tbody>
                {(orderData.cost_entries || []).map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono text-[11px]">{c.cost_category}</td>
                    <td className="px-3 py-1.5">{c.vendor || "—"}</td>
                    <td className="px-3 py-1.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider bg-slate-100">
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(c.allocated_slice ?? c.net_cost, c.currency)}</td>
                  </tr>
                ))}
                {(orderData.cost_entries || []).length === 0 && (
                  <tr><td colSpan={4} className="text-center py-4 text-[11px] text-slate-400">
                    No costs captured for this order yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Section>
  );
}


function Field({ label, value, bold, tone }) {
  return (
    <div className="st-panel p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] tabular-nums truncate ${bold ? "font-semibold" : ""} ${tone || ""}`}>{value}</div>
    </div>
  );
}


function MiniCustomerTable({ rows }) {
  return (
    <div className="st-panel divide-y divide-slate-100">
      {rows.map((r) => (
        <div key={r.master_company_id} className="p-3 text-[12px]"
             data-testid={`profit-mini-cust-${r.master_company_id}`}>
          <div className="flex items-center justify-between">
            <span className="font-semibold">{r.customer_name}</span>
            <span className={`text-[11px] tabular-nums font-semibold ${r.contribution_margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {r.margin_pct}%
            </span>
          </div>
          <div className="text-[10px] text-slate-500">
            Rev {fmt(r.revenue)} · Cost {fmt(r.cost)} · Margin {fmt(r.contribution_margin)}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="p-6 text-center text-[12px] text-slate-400">No customer data yet.</div>}
    </div>
  );
}


function MiniServiceTable({ rows }) {
  return (
    <div className="st-panel divide-y divide-slate-100">
      {rows.map((r) => (
        <div key={r.service_code} className="p-3 text-[12px]"
             data-testid={`profit-mini-svc-${r.service_code}`}>
          <div className="flex items-center justify-between">
            <span className="font-mono">{r.service_code}</span>
            <span className={`text-[11px] tabular-nums font-semibold ${r.contribution_margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {r.margin_pct}%
            </span>
          </div>
          <div className="text-[10px] text-slate-500">
            {r.transaction_count} txn · Rev {fmt(r.revenue)} · Cost {fmt(r.cost)}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="p-6 text-center text-[12px] text-slate-400">No service data yet.</div>}
    </div>
  );
}


function CustomerTable({ rows }) {
  return (
    <Section title={`Customer profitability (${rows.length})`} testId="profit-customer-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-right px-3 py-2">Revenue</th>
              <th className="text-right px-3 py-2">Cost</th>
              <th className="text-right px-3 py-2">Margin</th>
              <th className="text-right px-3 py-2">Margin %</th>
              <th className="text-right px-3 py-2">Target %</th>
              <th className="text-right px-3 py-2">Leakage</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.master_company_id} className="border-t border-slate-100"
                  data-testid={`profit-customer-row-${r.master_company_id}`}>
                <td className="px-3 py-1.5">{r.customer_name}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.revenue)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.cost)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(r.contribution_margin)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${r.below_target ? "text-amber-800" : "text-emerald-700"}`}>
                  {r.margin_pct}%
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.target_margin_pct}%</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">{fmt(r.leakage_value)}</td>
                <td className="px-3 py-1.5 text-[11px]">
                  {r.below_target ? <span className="text-amber-800">Below target</span> : <span className="text-emerald-700">On target</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


function ServiceTable({ rows }) {
  return (
    <Section title={`Service profitability (${rows.length})`} testId="profit-service-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Service</th>
              <th className="text-right px-3 py-2">Txn</th>
              <th className="text-right px-3 py-2">Revenue</th>
              <th className="text-right px-3 py-2">Cost</th>
              <th className="text-right px-3 py-2">Margin</th>
              <th className="text-right px-3 py-2">Margin %</th>
              <th className="text-right px-3 py-2">Target</th>
              <th className="text-right px-3 py-2">Avg rev/txn</th>
              <th className="text-right px-3 py-2">Avg cost/txn</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.service_code} className="border-t border-slate-100"
                  data-testid={`profit-service-row-${r.service_code}`}>
                <td className="px-3 py-1.5 font-mono text-[11px]">{r.service_code}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.transaction_count}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.revenue)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.cost)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(r.contribution_margin)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${r.below_target ? "text-amber-800" : "text-emerald-700"}`}>
                  {r.margin_pct}%
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.target_margin_pct}%</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.avg_revenue_per_txn)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.avg_cost_per_txn)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


function CostsTable({ rows, onReload, onAdd }) {
  const reverse = async (id) => {
    const reason = window.prompt("Reversal reason?");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/profitability/costs/${id}/reverse`, { reason });
      toast.success("Reversal created");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const changeStatus = async (id, status) => {
    try {
      await api.post(`/profitability/costs/${id}/status`, { status });
      toast.success(`→ ${status}`);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <Section title={`Cost entries (${rows.length})`}
             actions={
               onAdd && (
                 <Button size="sm" onClick={onAdd} data-testid="profit-add-cost-btn">
                   <Plus className="h-3 w-3 mr-1" /> Add cost
                 </Button>
               )
             }
             testId="profit-costs-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Order ID</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`profit-cost-row-${r.id}`}>
                <td className="px-3 py-1.5 font-mono text-[11px]">{r.order_id || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{r.cost_category}</td>
                <td className="px-3 py-1.5 text-[11px]">{r.vendor || "—"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.quantity}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(r.net_cost, r.currency)}</td>
                <td className="px-3 py-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider bg-slate-100">
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-1.5 pr-4 text-right space-x-1">
                  {r.status === "PENDING_APPROVAL" && (
                    <Button size="sm" variant="outline" onClick={() => changeStatus(r.id, "APPROVED")}
                            data-testid={`profit-cost-approve-${r.id}`}>Approve</Button>
                  )}
                  {["APPROVED", "ALLOCATED"].includes(r.status) && (
                    <Button size="sm" variant="ghost" className="text-rose-700"
                            onClick={() => reverse(r.id)}
                            data-testid={`profit-cost-reverse-${r.id}`}>Reverse</Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[12px] text-slate-400">
                No cost entries yet — add one from the toolbar.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


function UnallocatedTable({ data }) {
  return (
    <Section title={`Unallocated costs (${data.items.length}) · ${fmt(data.total_amount)}`}
             hint="Costs without an Order ID or allocation split. Do not let these disappear from profitability."
             testId="profit-unalloc-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Cost date</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`profit-unalloc-row-${r.id}`}>
                <td className="px-3 py-1.5 font-mono text-[11px]">{r.cost_category}</td>
                <td className="px-3 py-1.5 text-[11px]">{r.vendor || "—"}</td>
                <td className="px-3 py-1.5 text-[11px]">{r.cost_date?.slice(0, 10)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(r.net_cost, r.currency)}</td>
                <td className="px-3 py-1.5 text-[11px]">{r.status}</td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-[12px] text-slate-400">
                Nothing unallocated — every cost has a home.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


function LeakageTable({ rows, onReload, onAdd }) {
  const advance = async (id, status) => {
    try {
      await api.post(`/profitability/leakage/${id}/update`, { status });
      toast.success(`→ ${status.replace(/_/g, " ")}`);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <Section title={`Revenue leakage (${rows.length})`}
             hint="Where SUNTRONIX lost or risks losing revenue — every entry has an owner and a recovery path."
             actions={
               onAdd && (
                 <Button size="sm" onClick={onAdd} data-testid="profit-add-leakage-btn">
                   <AlertTriangle className="h-3 w-3 mr-1" /> Log leakage
                 </Button>
               )
             }
             testId="profit-leak-section">
      <div className="st-panel overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Order ID</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Cause</th>
              <th className="text-right px-3 py-2">Estimated</th>
              <th className="text-right px-3 py-2">Recovered</th>
              <th className="text-left px-3 py-2">Owner</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-slate-100"
                  data-testid={`profit-leak-row-${l.id}`}>
                <td className="px-3 py-1.5">{l.customer_name || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{l.order_id || "—"}</td>
                <td className="px-3 py-1.5 text-[11px]">{l.leakage_type.replace(/_/g, " ")}</td>
                <td className="px-3 py-1.5 text-[11px] text-slate-500">{l.cause || "—"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">{fmt(l.estimated_value, l.currency)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt(l.recovered_amount, l.currency)}</td>
                <td className="px-3 py-1.5 text-[11px]">{l.owner_email || "—"}</td>
                <td className="px-3 py-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${LEAK_TONE[l.status]}`}>
                    {l.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-1.5 pr-4 text-right space-x-1">
                  {l.status === "IDENTIFIED" && (
                    <Button size="sm" variant="outline"
                            onClick={() => advance(l.id, "RECOVERY_IN_PROGRESS")}
                            data-testid={`profit-leak-recover-${l.id}`}>Recover</Button>
                  )}
                  {["RECOVERY_IN_PROGRESS", "PARTIALLY_RECOVERED"].includes(l.status) && (
                    <Button size="sm" variant="outline"
                            onClick={() => advance(l.id, "RECOVERED")}
                            data-testid={`profit-leak-resolve-${l.id}`}>Mark recovered</Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-[12px] text-slate-400">
                No leakage records — nothing to chase.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


function ComingSoon({ tab }) {
  return (
    <div className="st-panel p-8 text-center text-[13px] text-slate-500"
         data-testid={`profit-coming-soon-${tab}`}>
      <Layers className="h-6 w-6 mx-auto mb-2 text-slate-300" />
      This surface ({tab}) opens up once the corresponding Phase 2 report is wired.
      Phase 1 delivers the core ledger — Order · Customer · Service · Cost · Unallocated · Leakage.
    </div>
  );
}


function AddCostDialog({ onClose, onAdded }) {
  const [category, setCategory] = useState("courier_forward");
  const [orderId, setOrderId] = useState("");
  const [vendor, setVendor] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState(0);
  const [isEstimated, setIsEstimated] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!unit) { toast.error("Unit cost required"); return; }
    setBusy(true);
    try {
      await api.post("/profitability/costs", {
        order_id: orderId || undefined,
        cost_category: category,
        vendor: vendor || undefined,
        quantity: Number(qty), unit_cost: Number(unit),
        is_estimated: isEstimated,
        source: "manual",
      });
      toast.success("Cost added");
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="profit-add-cost-dialog">
        <DialogHeader><DialogTitle>Add cost entry</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Order ID (optional)</div>
            <Input value={orderId} onChange={(e) => setOrderId(e.target.value)}
                   placeholder="Leave blank if unallocated"
                   data-testid="profit-add-cost-order" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="w-full border rounded h-9 px-2"
                    data-testid="profit-add-cost-category">
              {COST_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Vendor</div>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)}
                   data-testid="profit-add-cost-vendor" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Quantity</div>
              <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)}
                     data-testid="profit-add-cost-qty" />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Unit cost</div>
              <Input type="number" value={unit} onChange={(e) => setUnit(e.target.value)}
                     data-testid="profit-add-cost-unit" />
            </label>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={isEstimated}
                   onChange={(e) => setIsEstimated(e.target.checked)}
                   data-testid="profit-add-cost-est" />
            <span>Estimated (not yet actual)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="profit-add-cost-submit">
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AddLeakageDialog({ onClose, onAdded }) {
  const [orderId, setOrderId] = useState("");
  const [type, setType] = useState("completed_service_not_billed");
  const [value, setValue] = useState(0);
  const [cause, setCause] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!value) { toast.error("Estimated value required"); return; }
    setBusy(true);
    try {
      await api.post("/profitability/leakage", {
        order_id: orderId || undefined,
        leakage_type: type, estimated_value: Number(value),
        category: "process", cause: cause || undefined,
      });
      toast.success("Leakage recorded");
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="profit-add-leakage-dialog">
        <DialogHeader><DialogTitle>Log revenue leakage</DialogTitle></DialogHeader>
        <div className="space-y-3 text-[13px]">
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Order ID (optional)</div>
            <Input value={orderId} onChange={(e) => setOrderId(e.target.value)}
                   data-testid="profit-add-leak-order" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Type</div>
            <Input value={type} onChange={(e) => setType(e.target.value)}
                   data-testid="profit-add-leak-type" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Estimated value</div>
            <Input type="number" value={value} onChange={(e) => setValue(e.target.value)}
                   data-testid="profit-add-leak-value" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Cause</div>
            <textarea value={cause} onChange={(e) => setCause(e.target.value)}
                      className="w-full border rounded p-2" rows={3}
                      data-testid="profit-add-leak-cause" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="profit-add-leak-submit">
            Log
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
